import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { useSettings } from './SettingsContext';
import { buildTheme } from '../styles/theme';

interface ThemeColors {
  primary: string;
  secondary: string;
}

interface DynamicThemeContextType {
  colors: ThemeColors;
  updateColors: (primary: string, secondary: string) => void;
}

const DynamicThemeContext = createContext<DynamicThemeContextType | undefined>(undefined);

export const useDynamicTheme = () => {
  const context = useContext(DynamicThemeContext);
  if (!context) {
    throw new Error('useDynamicTheme must be used within a DynamicThemeProvider');
  }
  return context;
};

interface DynamicThemeProviderProps {
  children: ReactNode;
}

export const DynamicThemeProvider: React.FC<DynamicThemeProviderProps> = ({ children }) => {
  const { siteSettings } = useSettings();
  const [colors, setColors] = useState<ThemeColors>({
    primary: '#2563eb',
    secondary: '#1e40af'
  });

  useEffect(() => {
    // Não carregar settings na página de login para otimizar performance
    if (window.location.pathname === '/admin/login' || window.location.pathname === '/login') {
      return;
    }
    
    if (siteSettings?.customization) {
      const { primary_color, secondary_color } = siteSettings.customization;
      
      if (primary_color || secondary_color) {
        setColors({
          primary: primary_color || '#2563eb',
          secondary: secondary_color || '#1e40af'
        });
      }
    }
  }, [siteSettings]);

  const updateColors = (primary: string, secondary: string) => {
    setColors({ primary, secondary });
  };

  const theme = buildTheme(colors.primary, colors.secondary);

  return (
    <DynamicThemeContext.Provider value={{ colors, updateColors }}>
      <StyledThemeProvider theme={theme}>
        {children}
      </StyledThemeProvider>
    </DynamicThemeContext.Provider>
  );
}; 
